import { useContext, useEffect, useMemo, useState, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import { ArrowLeft, MapPin, List as ListIcon, Map as MapIcon, Navigation, Store as StoreIcon, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
const ExploreMap = lazy(() => import('@/components/ExploreMap'));

const DEFAULT_RADIUS_KM = 25;
const MAP_PIN_CAP = 150;

export default function Explore() {
  const navigate = useNavigate();
  const { selectedCity, userLocation, setUserLocation } = useContext(AuthContext);
  const [viewMode, setViewMode] = useState('map'); // map | list
  const [loading, setLoading] = useState(true);
  const [stores, setStores] = useState([]);
  const [query, setQuery] = useState('');
  const [radiusKm, setRadiusKm] = useState(DEFAULT_RADIUS_KM);
  const [activeDays, setActiveDays] = useState(7);
  const [locationLoading, setLocationLoading] = useState(false);

  const [selectedStore, setSelectedStore] = useState(null);
  const [storeProducts, setStoreProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  useEffect(() => {
    if (!selectedCity) navigate('/');
  }, [selectedCity, navigate]);

  const center = useMemo(() => {
    if (userLocation) return [userLocation.lat, userLocation.lng];
    if (selectedCity?.lat != null && selectedCity?.lng != null) return [selectedCity.lat, selectedCity.lng];
    return [0, 0];
  }, [userLocation, selectedCity]);

  const fetchStores = async () => {
    if (!selectedCity) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        city: selectedCity.id,
        lat: String(center[0]),
        lng: String(center[1]),
        radius_km: String(radiusKm),
        active_days: String(activeDays),
        limit: '250',
      });
      const resp = await fetch(`${API}/stores/nearby?${params.toString()}`);
      const data = await resp.json();
      if (!resp.ok) {
        toast.error(data.detail || 'Failed to load stores');
        setStores([]);
        return;
      }
      setStores(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error('Connection error while loading stores');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCity?.id, center[0], center[1], radiusKm, activeDays]);

  const requestLocation = () => {
    setLocationLoading(true);
    if (!navigator.geolocation) {
      toast.error('Geolocation not supported');
      setLocationLoading(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setUserLocation(loc);
        toast.success('Centered on your location');
        setLocationLoading(false);
      },
      () => {
        toast.error('Could not get your location');
        setLocationLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
    );
  };

  const fetchStoreProducts = async (storeId) => {
    setLoadingProducts(true);
    try {
      const resp = await fetch(`${API}/stores/${encodeURIComponent(storeId)}/products?limit=80`);
      const data = await resp.json();
      if (!resp.ok) {
        toast.error(data.detail || 'Failed to load store products');
        setStoreProducts([]);
        return;
      }
      setStoreProducts(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error('Connection error while loading store products');
    } finally {
      setLoadingProducts(false);
    }
  };

  const openStore = async (store) => {
    setSelectedStore(store);
    setStoreProducts([]);
    if (store?.store_id) await fetchStoreProducts(store.store_id);
  };

  const filteredStores = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return stores;
    return stores.filter((s) => {
      const name = String(s.store_name || '').toLowerCase();
      const addr = String(s.address || '').toLowerCase();
      return name.includes(q) || addr.includes(q);
    });
  }, [stores, query]);

  // Map rendering can get slow on low-end phones if we render too many pins.
  // Keep list view complete, but cap pins on the map.
  const mapStores = useMemo(() => {
    const arr = Array.isArray(filteredStores) ? [...filteredStores] : [];
    arr.sort((a, b) => {
      const ad = a?.distance_km;
      const bd = b?.distance_km;
      if (typeof ad === 'number' && typeof bd === 'number') return ad - bd;
      if (typeof ad === 'number') return -1;
      if (typeof bd === 'number') return 1;
      const ac = Number(a?.report_count || 0);
      const bc = Number(b?.report_count || 0);
      return bc - ac;
    });
    return arr.slice(0, MAP_PIN_CAP);
  }, [filteredStores]);

  const formatAge = (dt) => {
    if (!dt) return '';
    const date = new Date(dt);
    if (Number.isNaN(date.getTime())) return '';
    const diffMs = Date.now() - date.getTime();
    const h = Math.floor(diffMs / 3600000);
    const d = Math.floor(diffMs / 86400000);
    if (h < 24) return `${Math.max(h, 1)}h ago`;
    return `${d}d ago`;
  };

  return (
    <div className="min-h-screen bg-background pb-20">
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button onClick={() => navigate('/search')} variant="ghost" size="icon">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="text-center">
            <div className="text-xl font-heading font-bold text-primary">Explore</div>
            <div className="text-xs text-muted-foreground flex items-center justify-center gap-1">
              <MapPin className="w-3 h-3" />
              {selectedCity?.name}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="rounded-full"
              onClick={() => setViewMode(viewMode === 'map' ? 'list' : 'map')}
            >
              {viewMode === 'map' ? (
                <>
                  <ListIcon className="w-4 h-4 mr-2" /> List
                </>
              ) : (
                <>
                  <MapIcon className="w-4 h-4 mr-2" /> Map
                </>
              )}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="flex items-center gap-2 flex-1 min-w-[220px]">
            <StoreIcon className="w-4 h-4 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search stores or address..."
              className="h-9 text-sm"
            />
          </div>

          <Button
            variant="outline"
            size="sm"
            className="rounded-full"
            onClick={requestLocation}
            disabled={locationLoading}
          >
            <Navigation className="w-4 h-4 mr-2" />
            {locationLoading ? 'Locating...' : 'Near me'}
          </Button>

          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Radius</span>
            <select
              value={radiusKm}
              onChange={(e) => setRadiusKm(Number(e.target.value))}
              className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
            >
              {[5, 10, 25, 50, 100].map((r) => (
                <option key={r} value={r}>
                  {r} km
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Active</span>
            <select
              value={activeDays}
              onChange={(e) => setActiveDays(Number(e.target.value))}
              className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
            >
              {[7, 14, 30].map((d) => (
                <option key={d} value={d}>
                  {d} days
                </option>
              ))}
              <option value={0}>All</option>
            </select>
          </div>

          <Button variant="ghost" size="sm" onClick={fetchStores}>
            Refresh
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent" />
            <p className="mt-4 text-muted-foreground">Loading stores...</p>
          </div>
        ) : filteredStores.length === 0 ? (
          <Card className="p-6 text-sm text-muted-foreground">
            No stores found yet in this area. Report a price to add coverage.
          </Card>
        ) : viewMode === 'map' ? (
          <Suspense
            fallback={
              <Card className="p-6 text-sm text-muted-foreground">
                Loading map…
              </Card>
            }
          >
            <ExploreMap center={center} stores={mapStores} onOpenStore={openStore} />
          </Suspense>
        ) : (
          <div className="grid grid-cols-1 gap-3">
            {filteredStores.map((s) => (
              <Card
                key={s.store_id}
                className="p-4 hover:shadow-md transition-all cursor-pointer"
                onClick={() => openStore(s)}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="font-heading font-bold text-foreground">{s.store_name}</div>
                    {s.address && <div className="text-sm text-muted-foreground mt-1">{s.address}</div>}
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      {s.distance_km != null && <Badge variant="secondary">{s.distance_km} km</Badge>}
                      <span>{s.report_count} report{s.report_count === 1 ? '' : 's'}</span>
                      {s.last_reported && <span>• Updated {formatAge(s.last_reported)}</span>}
                    </div>
                  </div>
                  <Button size="sm" variant="outline" className="rounded-full">
                    Open
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>

      {selectedStore && (
        <div
          className="fixed inset-0 bg-black/50 z-[9999] flex items-end sm:items-center justify-center p-4"
          onClick={() => setSelectedStore(null)}
        >
          <div
            className="bg-card rounded-t-3xl sm:rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                  <div className="text-2xl font-heading font-bold text-foreground">
                    {selectedStore.store_name}
                  </div>
                  {selectedStore.address && (
                    <div className="text-sm text-muted-foreground mt-1">{selectedStore.address}</div>
                  )}
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    {selectedStore.distance_km != null && (
                      <Badge variant="secondary">{selectedStore.distance_km} km away</Badge>
                    )}
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${selectedStore.latitude},${selectedStore.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      Get directions
                    </a>
                  </div>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setSelectedStore(null)}>
                  ✕
                </Button>
              </div>

              <div className="flex items-center justify-between mb-3">
                <div className="font-heading font-bold text-foreground">Products at this store</div>
                <span className="text-xs text-muted-foreground">{storeProducts.length} items</span>
              </div>

              {loadingProducts ? (
                <div className="text-sm text-muted-foreground">Loading products...</div>
              ) : storeProducts.length === 0 ? (
                <Card className="p-4 text-sm text-muted-foreground">No approved products yet.</Card>
              ) : (
                <div className="space-y-2">
                  {storeProducts.map((p, idx) => (
                    <div
                      key={`${p.product_name}-${p.brand}-${p.size}-${idx}`}
                      className="flex items-center justify-between gap-3 rounded-xl border border-border bg-muted/30 px-3 py-2"
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-foreground truncate">
                          {p.product_name}
                          {p.brand && <span className="text-muted-foreground"> • {p.brand}</span>}
                          {p.size && <span className="text-muted-foreground"> ({p.size})</span>}
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-1">
                          {p.report_count} report{p.report_count === 1 ? '' : 's'}
                          {p.last_reported && ` • Updated ${formatAge(p.last_reported)}`}
                        </div>
                        {p.has_combo && (p.deal_label || p.deal_quantity || p.deal_price) && (
                          <div className="mt-2">
                            <Badge variant="secondary" className="text-[11px]">
                              Deal{p.deal_label ? `: ${p.deal_label}` : ''}
                            </Badge>
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-heading font-bold text-primary tabular-nums">
                          avg R{Number(p.avg_price).toFixed(2)}
                        </div>
                        {p.min_price != null && p.max_price != null && p.report_count > 1 && (
                          <div className="text-[11px] text-muted-foreground">
                            R{Number(p.min_price).toFixed(2)}–R{Number(p.max_price).toFixed(2)}
                          </div>
                        )}
                        <Button
                          size="xs"
                          variant="outline"
                          className="mt-2"
                          onClick={() => {
                            const term = p.product_name || '';
                            const params = new URLSearchParams({ q: term, store: selectedStore.store_name });
                            navigate(`/results?${params.toString()}`);
                          }}
                        >
                          View deals
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

